import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ImageUpload } from '../../components/shared/ImageUpload';
import { toast } from 'sonner';
import { Settings, Save, Loader2, Plus, Trash2, Eye, EyeOff, Key } from 'lucide-react';

export default function SystemSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);
  const [newBannerUrl, setNewBannerUrl] = useState('');

  useEffect(() => {
    api.get('/admin/settings').then(r => setSettings(r.data)).catch(() => toast.error('Failed to load settings'));
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      await api.put('/admin/settings', settings);
      toast.success('Settings saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (!settings) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-4 border-[#7551e8] border-t-transparent rounded-full animate-spin" /></div>;

  const update = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));
  const mask = (val) => val ? val.slice(0, 6) + '***' + val.slice(-4) : '';

  function addBannerImage(url) {
    if (!url) return;
    const images = [...(settings.banner_images || []), url];
    update('banner_images', images);
    setNewBannerUrl('');
  }

  function removeBannerImage(index) {
    const images = (settings.banner_images || []).filter((_, i) => i !== index);
    update('banner_images', images);
  }

  return (
    <div className="space-y-6" data-testid="system-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A] flex items-center gap-2"><Settings size={24} /> System Settings</h1>
          <p className="text-sm text-[#52525B] mt-1">Configure your site branding, banners, and integration credentials</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" data-testid="save-settings-btn">
          {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} Save All Changes
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Branding */}
        <Card className="p-6 border border-zinc-200 space-y-4">
          <h2 className="font-['Outfit'] font-medium text-lg">Branding</h2>
          <div>
            <Label>Site Name</Label>
            <Input value={settings.site_name || ''} onChange={e => update('site_name', e.target.value)} data-testid="setting-site-name" />
          </div>
          <div>
            <Label>Header Logo</Label>
            <ImageUpload value={settings.header_logo_url} onChange={v => update('header_logo_url', v)} folder="thumbnails" />
          </div>
          <div>
            <Label>Footer Logo</Label>
            <ImageUpload value={settings.footer_logo_url} onChange={v => update('footer_logo_url', v)} folder="thumbnails" />
          </div>
        </Card>

        {/* Contact & Social */}
        <Card className="p-6 border border-zinc-200 space-y-4">
          <h2 className="font-['Outfit'] font-medium text-lg">Contact & Social</h2>
          <div>
            <Label>Banner Title</Label>
            <Input value={settings.banner_title || ''} onChange={e => update('banner_title', e.target.value)} data-testid="setting-banner-title" />
          </div>
          <div>
            <Label>Banner Subtitle</Label>
            <Input value={settings.banner_subtitle || ''} onChange={e => update('banner_subtitle', e.target.value)} data-testid="setting-banner-subtitle" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Contact Email</Label>
              <Input value={settings.contact_email || ''} onChange={e => update('contact_email', e.target.value)} data-testid="setting-contact-email" />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input value={settings.contact_phone || ''} onChange={e => update('contact_phone', e.target.value)} data-testid="setting-contact-phone" />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label>Facebook</Label>
              <Input value={settings.social_facebook || ''} onChange={e => update('social_facebook', e.target.value)} placeholder="https://..." />
            </div>
            <div>
              <Label>Twitter</Label>
              <Input value={settings.social_twitter || ''} onChange={e => update('social_twitter', e.target.value)} placeholder="https://..." />
            </div>
            <div>
              <Label>Instagram</Label>
              <Input value={settings.social_instagram || ''} onChange={e => update('social_instagram', e.target.value)} placeholder="https://..." />
            </div>
          </div>
        </Card>

        {/* Banner Images */}
        <Card className="p-6 border border-zinc-200 space-y-4 md:col-span-2">
          <h2 className="font-['Outfit'] font-medium text-lg">Banner Images <span className="text-xs text-zinc-400 font-normal ml-2">({(settings.banner_images || []).length} images)</span></h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {(settings.banner_images || []).map((img, i) => (
              <div key={i} className="relative group">
                <img src={img} alt={`Banner ${i + 1}`} className="w-full h-28 object-cover rounded-lg border border-zinc-200" />
                <button onClick={() => removeBannerImage(i)} className="absolute top-1.5 right-1.5 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-banner-${i}`}>
                  <Trash2 size={12} />
                </button>
                <span className="absolute bottom-1.5 left-1.5 text-xs bg-black/60 text-white px-2 py-0.5 rounded">{i + 1}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={newBannerUrl} onChange={e => setNewBannerUrl(e.target.value)} placeholder="Paste image URL or upload below..." className="flex-1" data-testid="new-banner-url" />
            <Button variant="outline" onClick={() => addBannerImage(newBannerUrl)} disabled={!newBannerUrl} data-testid="add-banner-url-btn">
              <Plus size={14} className="mr-1" /> Add URL
            </Button>
          </div>
          <ImageUpload value="" onChange={v => { if (v) addBannerImage(v); }} folder="thumbnails" />
        </Card>

        {/* Integration Credentials */}
        <Card className="p-6 border border-zinc-200 space-y-4 md:col-span-2" data-testid="foundation-section">
          <h2 className="font-['Outfit'] font-medium text-lg">Foundation & Founder</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Foundation Logo</Label>
              <ImageUpload value={settings.foundation_logo_url} onChange={v => update('foundation_logo_url', v)} folder="thumbnails" />
            </div>
            <div>
              <Label>Founder Image</Label>
              <ImageUpload value={settings.founder_image_url} onChange={v => update('founder_image_url', v)} folder="thumbnails" />
            </div>
          </div>
        </Card>

        {/* Integration Credentials - original */}
        <Card className="p-6 border border-zinc-200 space-y-4 md:col-span-2" data-testid="credentials-section">
          <div className="flex items-center justify-between">
            <h2 className="font-['Outfit'] font-medium text-lg flex items-center gap-2"><Key size={18} /> Integration Credentials</h2>
            <Button variant="ghost" size="sm" onClick={() => setShowSecrets(!showSecrets)} data-testid="toggle-secrets-btn">
              {showSecrets ? <EyeOff size={14} className="mr-1" /> : <Eye size={14} className="mr-1" />} {showSecrets ? 'Hide' : 'Show'}
            </Button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-3 p-4 bg-zinc-50 rounded-lg">
              <h3 className="text-sm font-semibold text-[#0A0A0A]">Razorpay</h3>
              <div>
                <Label className="text-xs">Key ID</Label>
                <Input value={showSecrets ? (settings.razorpay_key_id || '') : mask(settings.razorpay_key_id)} onChange={e => update('razorpay_key_id', e.target.value)} disabled={!showSecrets} className="text-xs" data-testid="cred-razorpay-key" />
              </div>
              <div>
                <Label className="text-xs">Key Secret</Label>
                <Input type={showSecrets ? 'text' : 'password'} value={settings.razorpay_key_secret || ''} onChange={e => update('razorpay_key_secret', e.target.value)} disabled={!showSecrets} className="text-xs" data-testid="cred-razorpay-secret" />
              </div>
            </div>
            <div className="space-y-3 p-4 bg-zinc-50 rounded-lg">
              <h3 className="text-sm font-semibold text-[#0A0A0A]">Cloudinary</h3>
              <div>
                <Label className="text-xs">Cloud Name</Label>
                <Input value={showSecrets ? (settings.cloudinary_cloud_name || '') : mask(settings.cloudinary_cloud_name)} onChange={e => update('cloudinary_cloud_name', e.target.value)} disabled={!showSecrets} className="text-xs" data-testid="cred-cloudinary-name" />
              </div>
              <div>
                <Label className="text-xs">API Key</Label>
                <Input value={showSecrets ? (settings.cloudinary_api_key || '') : mask(settings.cloudinary_api_key)} onChange={e => update('cloudinary_api_key', e.target.value)} disabled={!showSecrets} className="text-xs" data-testid="cred-cloudinary-key" />
              </div>
              <div>
                <Label className="text-xs">API Secret</Label>
                <Input type={showSecrets ? 'text' : 'password'} value={settings.cloudinary_api_secret || ''} onChange={e => update('cloudinary_api_secret', e.target.value)} disabled={!showSecrets} className="text-xs" data-testid="cred-cloudinary-secret" />
              </div>
            </div>
            <div className="space-y-3 p-4 bg-zinc-50 rounded-lg">
              <h3 className="text-sm font-semibold text-[#0A0A0A]">Resend Email</h3>
              <div>
                <Label className="text-xs">API Key</Label>
                <Input type={showSecrets ? 'text' : 'password'} value={settings.resend_api_key || ''} onChange={e => update('resend_api_key', e.target.value)} disabled={!showSecrets} className="text-xs" data-testid="cred-resend-key" />
              </div>
              <div>
                <Label className="text-xs">Sender Email</Label>
                <Input value={showSecrets ? (settings.sender_email || '') : mask(settings.sender_email)} onChange={e => update('sender_email', e.target.value)} disabled={!showSecrets} className="text-xs" data-testid="cred-sender-email" />
              </div>
            </div>
          </div>
          <p className="text-xs text-zinc-400">Credentials are securely stored. Click "Show" to view or edit values.</p>
        </Card>
      </div>
    </div>
  );
}
